import { useState } from 'react'
import { useForm, Controller, type Control, type Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Button, Stack } from '@mui/material'
import { creaImpiantoConAzienda } from '../anagrafiche/impianto.api'
// SchemaImpianto/DatiImpianto vengono dal modulo schema, non da impianto.api: nel test
// impianto.api e' mockato per isolare creaImpiantoConAzienda e non esporta lo schema —
// serve comunque quello reale per la validazione Zod del form.
import { SchemaImpianto, type DatiImpianto } from '../anagrafiche/impianto.schema'
import { SelettoreTipologia } from '../anagrafiche/SelettoreTipologia'
import { CampoIndirizzo } from '../anagrafiche/CampoIndirizzo'
import { useServizi } from '../servizi/ServiziProvider'
import { TIPOLOGIE_DICHIARANTE } from '@praticheenergia/domain'
import { ETICHETTA_DICHIARANTE } from '../anagrafiche/etichette-dichiarante'

const RICHIEDONO_POTENZA = ['cessione_parziale_commerciale', 'cessione_totale']

const DEFAULT_VALORI: DatiImpianto = {
  azienda: { modo: 'nuova', ragioneSociale: '', codiceFiscale: '' },
  codiceDitta: '',
  tipologia: 'cessione_totale',
  tipologiaDichiarante: 'licenza_altra_officina',
  ubicazione: { comune: '', provincia: '', indirizzo: '' },
  ufficioContabilita: { comune: '', provincia: '', indirizzo: '' },
  note: '',
}

// azienda e un'unione discriminata (modo: 'nuova' | 'esistente'): react-hook-form
// non tipizza pulito i path al suo interno. Il form usa sempre modo: 'nuova'.
type DatiImpiantoNuova = DatiImpianto & {
  azienda: { modo: 'nuova'; ragioneSociale: string; codiceFiscale: string }
}

interface Props {
  readonly aperto: boolean
  readonly onChiudi: () => void
}

export function DialogoCreazioneImpianto({ aperto, onChiudi }: Props) {
  const { venditore } = useServizi()
  const [erroreSalvataggio, setErroreSalvataggio] = useState<string | null>(null)
  const { control, handleSubmit, watch, formState: { errors }, reset } = useForm<DatiImpianto>({
    // zodResolver infatti tipizza la sua Resolver su un'istanza leggermente diversa
    // dello stesso z.infer (il refine su tipologia confonde exactOptionalPropertyTypes):
    // cast puntuale, lo schema resta quello reale a runtime.
    resolver: zodResolver(SchemaImpianto) as unknown as Resolver<DatiImpianto>,
    defaultValues: DEFAULT_VALORI,
  })
  const controlNuova = control as unknown as Control<DatiImpiantoNuova>
  const tipologia = watch('tipologia')

  async function onSubmit(dati: DatiImpianto) {
    setErroreSalvataggio(null)
    try {
      await creaImpiantoConAzienda(dati)
      reset(DEFAULT_VALORI)
      onChiudi()
    } catch (e) {
      setErroreSalvataggio(e instanceof Error ? e.message : 'errore imprevisto')
    }
  }

  if (!aperto) return null

  return (
    <Dialog open={aperto} onClose={onChiudi} fullWidth maxWidth="sm">
      <DialogTitle>Nuovo impianto</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Controller
            name="azienda.ragioneSociale"
            control={controlNuova}
            render={({ field }) => (
              <TextField
                {...field}
                label="Ragione sociale"
                error={!!(errors.azienda as { ragioneSociale?: unknown } | undefined)?.ragioneSociale}
                helperText={(errors.azienda as { ragioneSociale?: { message?: string } } | undefined)?.ragioneSociale?.message}
              />
            )}
          />
          <Controller
            name="azienda.codiceFiscale"
            control={controlNuova}
            render={({ field }) => (
              <TextField
                {...field}
                label="Codice fiscale"
                error={!!(errors.azienda as { codiceFiscale?: unknown } | undefined)?.codiceFiscale}
                helperText={(errors.azienda as { codiceFiscale?: { message?: string } } | undefined)?.codiceFiscale?.message}
              />
            )}
          />
          <Controller
            name="codiceDitta"
            control={control}
            render={({ field }) => (
              <TextField {...field} label="Codice ditta" error={!!errors.codiceDitta} helperText={errors.codiceDitta?.message} />
            )}
          />
          <Controller
            name="tipologia"
            control={control}
            render={({ field }) => (
              <SelettoreTipologia venditore={venditore} valore={field.value} onChange={field.onChange} />
            )}
          />
          <Controller
            name="tipologiaDichiarante"
            control={control}
            render={({ field }) => (
              <TextField {...field} select label="Tipologia dichiarante">
                {TIPOLOGIE_DICHIARANTE.map((t) => (
                  <MenuItem key={t} value={t}>
                    {ETICHETTA_DICHIARANTE[t]}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            name="ubicazione"
            control={control}
            render={({ field }) => (
              <CampoIndirizzo etichetta="Ubicazione impianto" valore={field.value} onChange={field.onChange} />
            )}
          />
          <Controller
            name="ufficioContabilita"
            control={control}
            render={({ field }) => (
              <CampoIndirizzo etichetta="Ufficio di contabilita" valore={field.value} onChange={field.onChange} />
            )}
          />
          {RICHIEDONO_POTENZA.includes(tipologia) && (
            <Controller
              name="potenzaNominale"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value ?? ''}
                  type="number"
                  label="Potenza nominale"
                  onChange={(e) => field.onChange(e.target.value === '' ? undefined : Number(e.target.value))}
                  error={!!errors.potenzaNominale}
                  helperText={errors.potenzaNominale?.message}
                />
              )}
            />
          )}
          {erroreSalvataggio && <p role="alert">{erroreSalvataggio}</p>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onChiudi}>Annulla</Button>
        <Button variant="contained" onClick={handleSubmit(onSubmit)}>
          Salva
        </Button>
      </DialogActions>
    </Dialog>
  )
}
